// Update lifecycle — signal 2: is a newer `@vortex-os/base` published than the
// one installed? This is the NETWORK half of version awareness (signal 1, the
// local "templates not yet applied" notice, lives in session-start + update.ts).
//
// Locked behavior (maintainer decisions):
//   - on by default, checked ONCE PER SESSION (the caller gates on
//     `updates.check`; session start fires once per session, so a check there
//     is once per session — no daily cache, no throttle);
//   - hard-bounded and OFFLINE-SILENT: any failure (offline, no npm, timeout,
//     junk output) returns a result with `latest: null` / `newer: false` and is
//     simply not surfaced — it never blocks or crashes the session report;
//   - it only ever READS. The actual update is consent-gated and agent-driven:
//     this emits the exact install command; the agent asks, then runs it.
//
// Why `npm view` (not a hardcoded HTTPS GET to registry.npmjs.org): it inherits
// the user's real npm registry / proxy / auth, so the "latest" we report is one
// they can actually install — a private-registry / corporate-mirror user is
// served correctly, and we never leak a request to npmjs when their org uses a
// mirror. We shell out the same way the session report already shells out to
// git. (Empirically, on Windows + Node 24, `npm` is `npm.cmd` and cannot be
// spawned without a shell — so we run a fixed command STRING via `execSync`,
// which uses a shell on every platform and avoids the args-with-shell
// deprecation. The command has no interpolated/user input, so there is no
// injection surface.)

import { execSync } from "node:child_process";
import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import type { ModuleContext } from "@vortex-os/core";
import { resolveTemplatesDir } from "./commands/vortex.js";

/** The framework package whose published version we track. */
const PKG = "@vortex-os/base";
/**
 * Hard ceiling on the npm query (ms) — a backstop only. With `fetch-retries=0`
 * + a tight `fetch-timeout` (set in the env in `queryNpmLatest`), npm fails
 * fast offline well before this fires, so the timeout/kill — and its
 * Windows orphaned-grandchild risk — essentially never triggers.
 */
const NPM_TIMEOUT_MS = 4000;

export interface UpdateCheckResult {
  readonly package: string;
  /** Installed base version (from the shipped template index), or null if unknown. */
  readonly installed: string | null;
  /** Latest published version, or null when the check was skipped/failed/offline. */
  readonly latest: string | null;
  /** True only when `latest` is a strictly-greater stable version than `installed`. */
  readonly newer: boolean;
  /** Exact command to apply the update — present only when `newer`. */
  readonly command: string | null;
  /** Disclosed: where the check looked (honest about the network contact). */
  readonly registry: string;
}

/**
 * Installed base version = the `baseVersion` recorded in the shipped template
 * index (`templates/manifest.json`). This is signal 1's source of truth too,
 * and it travels with the running binary, so it can't disagree with the version
 * actually executing (local or global/npx). Unreadable/invalid → null.
 */
export function readInstalledBaseVersion(
  templatesDir: string | null = resolveTemplatesDir(),
): string | null {
  if (!templatesDir) return null;
  try {
    const m = JSON.parse(readFileSync(join(templatesDir, "manifest.json"), "utf8")) as { baseVersion?: unknown };
    return typeof m.baseVersion === "string" && parseCore(m.baseVersion) ? m.baseVersion.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Query the registry for the latest published `@vortex-os/base` version via
 * `npm view`. Bounded by `NPM_TIMEOUT_MS`; any failure (offline, no npm,
 * timeout, non-JSON) returns null so the caller stays silent. Never throws.
 */
export function queryNpmLatest(repoRoot: string, pkg: string = PKG): string | null {
  // Guard the only interpolated value: a package name is [@a-z0-9._/-] only, so a
  // malformed/hostile entry can never inject shell. (Callers pass package.json keys.)
  if (!/^@?[a-z0-9][a-z0-9._/-]*$/i.test(pkg)) return null;
  try {
    const out = execSync(`npm view ${pkg} version --json`, {
      cwd: repoRoot,
      encoding: "utf8",
      timeout: NPM_TIMEOUT_MS,
      stdio: ["ignore", "pipe", "ignore"],
      windowsHide: true,
      // Quiet npm's chatter, and make it FAIL FAST offline: no retry backoff
      // (default 2 retries with exponential backoff would block to the full
      // timeout) and a tight per-request timeout — so a flaky/offline network
      // never stalls the session report and never orphans a stuck npm child.
      env: {
        ...process.env,
        NO_UPDATE_NOTIFIER: "1",
        npm_config_fund: "false",
        npm_config_audit: "false",
        npm_config_fetch_retries: "0",
        npm_config_fetch_timeout: "2000",
      },
    });
    const v = JSON.parse(out.trim()) as unknown;
    return typeof v === "string" && parseCore(v) ? v.trim() : null;
  } catch {
    return null;
  }
}

/** Hard ceiling for `npm audit` (ms) — more generous than a version query (audit POSTs the
 * whole dependency tree), still fail-fast offline via the env below. */
const AUDIT_TIMEOUT_MS = 20000;

export interface AuditSummary {
  readonly total: number;
  readonly critical: number;
  readonly high: number;
  readonly moderate: number;
  readonly low: number;
}

/**
 * Run `npm audit --json` in the instance root and return severity counts, or null on ANY
 * failure (offline, no npm, no lockfile, timeout, non-JSON) so the caller stays silent —
 * never throws. NOTE: `npm audit` EXITS NON-ZERO when it finds vulnerabilities; the JSON
 * summary is still on stdout, which `execSync` exposes via the thrown error's `.stdout`.
 */
export function queryNpmAudit(repoRoot: string): AuditSummary | null {
  if (!existsSync(join(repoRoot, "package-lock.json"))) return null; // audit needs a lockfile
  let raw: string | null = null;
  try {
    raw = execSync("npm audit --json", {
      cwd: repoRoot,
      encoding: "utf8",
      timeout: AUDIT_TIMEOUT_MS,
      stdio: ["ignore", "pipe", "ignore"],
      windowsHide: true,
      env: {
        ...process.env,
        NO_UPDATE_NOTIFIER: "1",
        npm_config_fund: "false",
        npm_config_fetch_retries: "0",
        npm_config_fetch_timeout: "8000",
      },
    });
  } catch (e) {
    const out = (e as { stdout?: string }).stdout;
    raw = typeof out === "string" && out ? out : null;
  }
  if (!raw) return null;
  try {
    const v = (JSON.parse(raw) as { metadata?: { vulnerabilities?: Record<string, number> } })?.metadata
      ?.vulnerabilities;
    if (!v || typeof v.total !== "number") return null;
    return {
      total: v.total,
      critical: v.critical ?? 0,
      high: v.high ?? 0,
      moderate: v.moderate ?? 0,
      low: v.low ?? 0,
    };
  } catch {
    return null;
  }
}

/**
 * Parse the numeric core + whether a prerelease tag is present, or null if the
 * string is not a clean dotted triple. STRICT (it is the sole validator now):
 * trims, strips a leading `v` and `+build` metadata, then requires EXACTLY
 * three core fields each matching `^\d+$` — so `"1.0.0.0"`, `"1.2.3junk"`,
 * `"1e1.0.0"`, `"0x10.0.0"`, trailing space/newline, etc. all map to null
 * rather than a truncated/coerced comparison (the comparator is trust-critical:
 * unparseable must never read as an update).
 */
// Whole-string semver match (the SOLE validator): optional leading `v`,
// exactly three numeric core fields, an optional WELL-FORMED prerelease
// (dot-separated non-empty `[0-9A-Za-z-]` identifiers), an optional well-formed
// `+build` tag — and nothing else. Surrounding whitespace, a 4th field, junk
// (`1e1`, `0x10`, `1.2.3junk`), or a malformed/empty prerelease (`1.0.0-`,
// `1.0.0-@@`) all fail to match → null, so an unparseable value is never
// compared as a version.
const SEMVER_FULL =
  /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

function parseCore(v: string): { nums: [number, number, number]; pre: boolean } | null {
  if (typeof v !== "string") return null;
  const m = SEMVER_FULL.exec(v);
  if (!m) return null;
  const nums: [number, number, number] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (nums.some((n) => !Number.isSafeInteger(n))) return null; // reject absurdly long cores
  return { nums, pre: m[4] !== undefined };
}

/**
 * Compare two versions: -1 (a<b), 0 (a==b), 1 (a>b), or null if either is
 * unparseable. Numeric core left-to-right; on an equal core a RELEASE outranks
 * a PRERELEASE of the same core. We deliberately do NOT implement full SemVer
 * §11 prerelease-vs-prerelease ordering — two prereleases of the same core
 * compare EQUAL here. That is fine because the surfacing policy
 * ({@link isStableUpdate}) only ever offers a STABLE `latest`, so a
 * prerelease→prerelease "upgrade" is never surfaced regardless; the §11
 * tag-ordering is the most bug-prone part, so omitting it removes risk.
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 | null {
  const pa = parseCore(a);
  const pb = parseCore(b);
  if (!pa || !pb) return null;
  for (let i = 0; i < 3; i++) {
    if (pa.nums[i]! !== pb.nums[i]!) return pa.nums[i]! > pb.nums[i]! ? 1 : -1;
  }
  if (pa.pre === pb.pre) return 0;
  return pa.pre ? -1 : 1; // same core: release (no prerelease) is newer
}

/** True only when `latest` is a strictly-greater version than `installed`. Null-safe → false. */
export function isNewer(latest: string | null, installed: string | null): boolean {
  if (!latest || !installed) return false;
  return compareSemver(latest, installed) === 1;
}

/**
 * The SURFACING policy: only offer a STABLE newer release. A prerelease
 * `latest` (e.g. `2.0.0-beta.1`) is never surfaced — VortEX never nudges users
 * onto a beta — even when its core is higher. Upgrading FROM a prerelease TO a
 * stable release of the same-or-higher core IS surfaced (because `latest` is
 * then stable). Null/unparseable → false. This is what `checkBaseUpdate` uses
 * to decide `newer`.
 */
export function isStableUpdate(latest: string | null, installed: string | null): boolean {
  if (!latest || !installed) return false;
  const p = parseCore(latest);
  if (!p || p.pre) return false; // unparseable or prerelease latest → don't surface
  return compareSemver(latest, installed) === 1;
}

/**
 * The exact, copy-pasteable command to apply the update, detected from the
 * instance: package manager by lockfile, local vs global by whether base sits
 * in this folder's node_modules. Always chained with `npx vortex update` so the
 * new package's templates get applied right after the install. Pure (no
 * network); the string is surfaced verbatim and the CLI never runs it.
 */
export function buildInstallCommand(repoRoot: string): string {
  const has = (f: string) => existsSync(join(repoRoot, f));
  const local = existsSync(join(repoRoot, "node_modules", "@vortex-os", "base"));
  let installPart: string;
  if (!local) {
    // Not a local dependency → a global install. Use npm's global form: the
    // folder's lockfile describes the LOCAL project, not the global install, so
    // honoring it here would wrongly `pnpm add`/`yarn add` base INTO the notes
    // folder. npm is the safe default and the command is shown for review.
    installPart = `npm i -g ${PKG}@latest`;
  } else if (has("pnpm-lock.yaml")) {
    installPart = `pnpm add ${PKG}@latest`;
  } else if (has("yarn.lock")) {
    installPart = `yarn add ${PKG}@latest`;
  } else {
    installPart = `npm i ${PKG}@latest`;
  }
  return `${installPart} && npx vortex update`;
}

/**
 * Run the once-per-session update check. The CALLER decides whether to call
 * (session start gates on `updates.check`; `vortex check-updates` forces it).
 * Never throws — returns a result with `latest: null` / `newer: false` when the
 * registry can't be reached, so the caller simply shows nothing.
 */
export function checkBaseUpdate(ctx: ModuleContext): UpdateCheckResult {
  const installed = readInstalledBaseVersion();
  const latest = queryNpmLatest(ctx.repoRoot);
  const newer = isStableUpdate(latest, installed);
  return {
    package: PKG,
    installed,
    latest,
    newer,
    command: newer ? buildInstallCommand(ctx.repoRoot) : null,
    registry: "the npm registry (per your npm config)",
  };
}
